import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FlatPickrOutputOptions } from 'angularx-flatpickr/lib/flatpickr.directive';
import { Observable, of, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { AttendanceService } from 'src/app/employee/services/attendance.service';
import { SubscriptionService } from '../../../../setting/services/subscription.service';
import { FlatpickrHelper } from '../../../../shared/common/FlatpickrHelper';
import { FlatpickrLocaleService } from '../../../../shared/services/flatpickr-locale.service';
import { AvailableFeatures } from '../../../models/AvailableFeatures';
import { Employee } from '../../../models/Employee';
import { EmployeeDateTime } from '../../../models/EmployeeDateTime';
import { EmployeeRequiredDataUpdates } from '../../../models/EmployeeRequiredDataUpdates';
import { EmployeeService } from '../../../services/employee.service';
import { CreateEditEmployeeDatetimeModalComponent } from '../../partials/create-edit-employee-datetime-modal/create-edit-employee-datetime-modal.component';

export interface EmployeeAttendanceOverview {
    dates: {
        absence: {
            from: string;
            to: string;
            datetime_ids: string
            total: string;
        };
        date: string;
        is_holiday: boolean;
        is_weekend: boolean;
        saldo: string;
        work: {
            from: string;
            to: string;
            datetime_ids: string;
            total: string;
            datetime_project_IDs?: number[];
            datetime_project_names?: string[];
            meal_vouchers?: boolean;
        };
    }[];
    employee: Employee;
    summary: {
        protection_state: {
            protected_ratio: number;
            protected_records: number;
            total_records: number;
        },
        whole_month: {
            expected: string;
            expected_with_holidays: string;
            real: string;
            saldo: string;
            holidays_work: string;
            weekends_work: string;
            night_work: string;
            holidays_taken: string;
            holidays_not_work: string;
            worktime_summaries: [key: string, value: number];
            absence_summaries: [key: string, value: number];
            neutral_summaries: [key: string, value: number];
            meal_vouchers?: number;
            meal_allowance?: number;
        },
        project_summaries?: [
            { name: string, total: number }
        ]
    };
    month: number;
    year: number;
    is_uneven: boolean;
    meal_vouchers: boolean;
    meal_allowance: boolean;
    employees_datetimes_projects: boolean;
    prev_next?: {
        next?: Employee;
        prev?: Employee;
    };
    attendance_locked: boolean;
    is_approvable_by_employee: boolean;
    is_approval_requested: boolean;
    is_approved_by_employee: boolean;
    show_bonus_modal: boolean;
}

@Component({
    selector: 'app-employee-attendance-document-employee-detail',
    templateUrl: './employee-attendance-document-employee-detail.component.html',
    styleUrls: ['./employee-attendance-document-employee-detail.component.css']
})
export class EmployeeAttendanceDocumentEmployeeDetailComponent implements OnInit, OnDestroy {
    public employeeID: number;
    public month: number;
    public year: number;
    public nextYear: number;
    public nextMonth: number;
    public nextActive: boolean;
    public prevYear: number;
    public prevMonth: number;
    public from: Date;
    public to: Date;
    public data$: Observable<EmployeeAttendanceOverview>;
    public availableFeatures$: Observable<AvailableFeatures>;
    public requiredDataUpdates$: Observable<EmployeeRequiredDataUpdates>;
    public locale$ = this._flatpickrLocale.currentFlatpickrLocale$;
    public rowChanged: { [key: string]: boolean }[] = [];

    private _subscriptions = new Subscription();

    public constructor(
        public fpHelper: FlatpickrHelper,
        private _flatpickrLocale: FlatpickrLocaleService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _attendanceService: AttendanceService,
        private _modalService: NgbModal,
        private _subscriptionService: SubscriptionService,
        private _employeeService: EmployeeService,
        protected _changeDetectorRef: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.init();
    }

    public ngOnDestroy(): void {
        this._subscriptions?.unsubscribe();
    }

    public init(): void {
        this.availableFeatures$ = this._subscriptionService.availableFeatures$;

        this.employeeID = parseInt(this._route.snapshot.params.id, 10);
        const routeMonth = this._route.snapshot.queryParamMap.get('month');
        const routeYear = this._route.snapshot.queryParamMap.get('year');

        if (routeMonth === 'current') {
            this.month = new Date().getMonth() + 1;
        } else {
            this.month = parseInt(routeMonth, 10);
        }

        if (routeYear === 'current') {
            this.year = new Date().getFullYear();
        } else {
            this.year = parseInt(routeYear, 10);
        }

        this.from = new Date(this.year, this.month - 1, 1);
        this.to = new Date(this.year, this.month, 0);

        this.nextYear = this.month === 12 ? this.year + 1 : this.year;
        this.nextMonth = this.month === 12 ? 1 : this.month + 1;
        this.nextActive = new Date(this.nextYear, this.nextMonth - 1, 1) <= new Date();
        this.prevYear = this.month === 1 ? this.year - 1 : this.year;
        this.prevMonth = this.month === 1 ? 12 : this.month - 1;
        this.data$ = this._attendanceService.getAttendanceOverviewByEmployee(this.employeeID, this.month, this.year)
            .pipe(
                tap(data => {
                    data.dates.forEach((date, index) => this.rowChanged[index] = {});
                })
            );

        this.getRequiredDataUpdates();
    }

    public goToMonthOverview(year: number, month: number): void {
        this._router.navigate(
            ['employee/attendance/documents/detail/employee', this.employeeID],
            {queryParams: {month, year}}
        ).then(() => {
            this.init();
        }, () => {});
    }

    public downloadAsPDF(year: number, month: number, data: EmployeeAttendanceOverview): void {
        const filename = `${year}-${month} - ${data.employee.fullname}.pdf`;
        this._attendanceService.downloadEmployeeAttendancePdf(this.employeeID, filename, month, year);
    }

    public sendToAbra(year: number, month: number): void {
        this._attendanceService.sendToAbra(this.employeeID, month, year)
            .then(() => {
                this.init();
            }, () => {});
    }

    public recalculateDatetimes() {
        const from = new Date(this.year, this.month - 1, 1);
        const formattedFrom = `${from.getFullYear()}-${String(from.getMonth() + 1).padStart(2, '0')}-${String(from.getDate()).padStart(2, '0')}`;
        const to = new Date(this.year, this.month, 0);
        const formattedTo = `${to.getFullYear()}-${String(to.getMonth() + 1).padStart(2, '0')}-${String(to.getDate()).padStart(2, '0')}`;
        this._attendanceService.recalculateDatetimes([this.employeeID], null, formattedFrom, formattedTo)
            .then(
                result => {
                    if (result) {
                        this.init();
                    }
                },
                () => { }
            );
    }

    public navigateToEntity(id: number): void {
        this._route.params.subscribe(params => {
            const oldID = params.id;
            void this._router.navigateByUrl(this._router.url.replace(oldID, id.toString()));
            this.init();
        });
    }

    public openDatetimeModal(datetimeID?: any, date?: string): void {
        const modalRef = this._modalService.open(CreateEditEmployeeDatetimeModalComponent, {centered: true});
        modalRef.componentInstance.employeeID = this.employeeID;
        modalRef.componentInstance.isShift = false;

        if (datetimeID) {
            modalRef.componentInstance.isLoading = true;
            this._attendanceService.getEmployeeDateTimeByID(this.employeeID, datetimeID, 'extend')
                .subscribe(dateTime => {
                    modalRef.componentInstance.employeeDateTime = dateTime;
                    modalRef.componentInstance.init();
                    modalRef.componentInstance.isLoading = false;

                    modalRef.result
                        .then(
                            () => {
                                this.refreshOverviewWithoutLoader();
                            },
                            () => {}
                        );
                });
        } else {
            modalRef.componentInstance.start = date;
            modalRef.componentInstance.end = date;

            modalRef.result
                .then(
                    () => {
                        this.refreshOverviewWithoutLoader();
                    }, () => {}
                );
        }
    }

    public refreshOverviewWithoutLoader(): void {
        this._subscriptions.add(this._attendanceService.getAttendanceOverviewByEmployee(this.employeeID, this.month, this.year)
            .subscribe(data => {
                this.data$ = of(data);
                data.dates.forEach((date, index) => this.rowChanged[index] = {});
            }));
    }

    public onSubmitRow(rowIndex: any, tableUpdateForm: NgForm, datetimeID: any, date: any, type: 'absence' | 'attendance', indexInRow: number): void {
        const row: { [key: string]: boolean } = tableUpdateForm.controls[rowIndex].value;

        const fromLabel = (type === 'attendance' ? 'from' : 'from_absence') + indexInRow;
        const toLabel = (type === 'attendance' ? 'to' : 'to_absence') + indexInRow;

        const rowValue = {
            set_on: 'TIME',
            from: `${date} ${row[fromLabel]}:00`,
            to: `${date} ${row[toLabel]}:00`,
            state: 'APPROVED'
        };

        this._attendanceService.saveEmployeeDateTime(rowValue as EmployeeDateTime, this.employeeID, datetimeID, 'ATTENDANCE')
            .then(result => {
                this.refreshOverviewWithoutLoader();
            });
    }

    public onFromToChange($event: FlatPickrOutputOptions, rowIndex: number, actualValue: any, control: 'from' | 'to' | 'from_absence' | 'to_absence', indexInRow: number): void {
        this.rowChanged[rowIndex][control + indexInRow as string] = $event.dateString !== actualValue;
    }

    public getRequiredDataUpdates(): void {
        this.requiredDataUpdates$ = this._employeeService.getRequiredDataUpdates(this.employeeID);
    }

    public triggerAttendanceApproval(): void {
        const button = document.getElementById('attendance-approvement-button');
        button.click();
    }
}
